import React, { useState, useEffect } from 'react'
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Chip,
  IconButton,
  Tooltip,
  LinearProgress,
  Alert,
} from '@mui/material'
import {
  ArrowBack as BackIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  Print as PrintIcon,
  ArrowUpward as InIcon,
  ArrowDownward as OutIcon,
  LocationOn as LocationIcon,
  Search as SearchIcon,
} from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'

import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import toast from 'react-hot-toast'
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Legend, ResponsiveContainer } from 'recharts'
import { usePagination, useLoading, useTableFilters } from '../../hooks'
import { StatusChip } from '../../components/common/Chips'
import { exportToCSV } from '../../utils/exportUtils'
import { formatDate } from '../../utils/formatters'
import { attendanceApi } from '../../api/attendanceApi'
import TableSkeleton from '../../components/common/Skeleton/TableSkeleton'

const AttendanceHistory = () => {
  const navigate = useNavigate()
  const { user, orgId } = useSelector((state) => state.auth)
  const { loading, withLoading } = useLoading()
  const pagination = usePagination()
  const [selectedMonth, setSelectedMonth] = useState(new Date())
  const { filters, updateFilter } = useTableFilters({
    status: 'all',
    search: '',
    year: new Date().getFullYear().toString(),
  })
  const [totalRecords, setTotalRecords] = useState(0)

  const [attendanceHistory, setAttendanceHistory] = useState({
    summary: {
      attendanceRate: 0,
      presentDays: 0,
      absentDays: 0,
      totalOvertime: 0,
      averageWorkHours: 0,
      totalWorkingDays: 0,
      lateArrivals: 0,
      leavesTaken: 0,
    },
    monthlyData: [],
    detailedRecords: [],
  })

  useEffect(() => {
    fetchAttendanceHistory()
  }, [filters.year, selectedMonth, pagination.page, pagination.rowsPerPage, filters.status, filters.search])

  const fetchAttendanceHistory = async () => {
    withLoading(async () => {
      try {
        const params = {
          page: pagination.page + 1,
          limit: pagination.rowsPerPage
        }

        const response = await attendanceApi.getAttendanceHistory(params, {
          'X-Employee-ID': user?.id,
          'X-Org-ID': orgId
        })

        if (response.data) {
          const { records, pagination: paginationData } = response.data
          setAttendanceHistory(prev => ({
            ...prev,
            detailedRecords: records.map(record => ({
              id: record.id,
              date: record.date,
              day: record.day,
              checkIn: record.check_in || '-',
              checkOut: record.check_out || '-',
              status: record.status,
              workHours: record.work_hours,
              overtime: record.overtime,
              location: record.location || '-',
              notes: '-'
            }))
          }))
          setTotalRecords(paginationData.total)
        }

        // Fetch Employee Stats
        const statsResponse = await attendanceApi.getEmployeeStats({
          'X-Org-ID': orgId
        })

        if (statsResponse.data) {
          const stats = statsResponse.data
          setAttendanceHistory(prev => ({
            ...prev,
            summary: {
              ...prev.summary,
              attendanceRate: stats.attendance_rate || 0,
              presentDays: stats.present_days || 0,
              absentDays: stats.absent_days || 0,
              totalOvertime: stats.total_overtime || 0,
              averageWorkHours: stats.average_work_hrs || 0,
            }
          }))
        }

        // Fetch Dedicated Attendance Summary
        const summaryResponse = await attendanceApi.getAttendanceSummary({
          'X-Org-ID': orgId
        })

        if (summaryResponse.data) {
          const summary = summaryResponse.data
          setAttendanceHistory(prev => ({
            ...prev,
            summary: {
              ...prev.summary,
              totalWorkingDays: summary.total_working_days || 0,
              absentDays: summary.absent ?? prev.summary.absentDays,
              presentDays: summary.present_days ?? prev.summary.presentDays,
              lateArrivals: summary.late_arrivals || 0,
              leavesTaken: summary.leaves_taken || 0,
            }
          }))
        }

        // Fetch Monthly Attendance Overview
        const overviewResponse = await attendanceApi.getMonthlyAttendanceOverview({
          'X-Org-ID': orgId
        })

        if (overviewResponse.data) {
          const overviewData = overviewResponse.data
          const mappedMonthlyData = Object.entries(overviewData).map(([month, data]) => ({
            month,
            present: data.present_days || 0,
            absent: data.absent_days || 0,
            late: data.late_arrivals || 0
          }))

          setAttendanceHistory(prev => ({
            ...prev,
            monthlyData: mappedMonthlyData
          }))
        }
      } catch (error) {
        console.error('Error fetching history:', error)
        toast.error('Failed to fetch attendance history')
      }
    })
  }

  const calculateAttendanceRate = () => {
    return attendanceHistory.summary.attendanceRate
  }

  const filteredRecords = attendanceHistory.detailedRecords

  // Pagination for filtered records
  const paginatedRecords = filteredRecords

  const handleExportData = () => {
    toast.success('Exporting attendance data...')
    const columns = [
      { label: 'Date', key: 'date' },
      { label: 'Day', key: 'day' },
      { label: 'Check In', key: 'checkIn' },
      { label: 'Check Out', key: 'checkOut' },
      { label: 'Work Hours', key: 'workHours' },
      { label: 'Overtime', key: 'overtime' },
      { label: 'Status', key: 'status' },
      { label: 'Location', key: 'location' },
      { label: 'Notes', key: 'notes' }
    ]
    exportToCSV(filteredRecords, columns, 'my-attendance-history')
  }

  const handlePrintReport = () => {
    window.print()
  }

  return (
    <Box sx={{ p: { xs: 1, md: 3 } }}>
      <Paper sx={{ p: { xs: 2, md: 3 }, borderRadius: 3, bgcolor: 'background.paper', boxShadow: 3 }}>
        <Box sx={{ mb: 4 }}>
          <Box sx={{ mb: 2 }}>
            <Box>
              <Typography variant="h4" gutterBottom>
                Attendance History
              </Typography>
              <Typography variant="body1" color="textSecondary">
                Detailed view of your attendance records and statistics
              </Typography>
            </Box>
          </Box>

          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card variant="outlined">
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h3" color="primary" sx={{ fontWeight: 'bold', mb: 1 }}>
                    {calculateAttendanceRate()}%
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Attendance Rate
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card variant="outlined">
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h3" sx={{ fontWeight: 'bold', mb: 1, color: '#4caf50' }}>
                    {attendanceHistory.summary.presentDays}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Present Days
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card variant="outlined">
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h3" sx={{ fontWeight: 'bold', mb: 1, color: '#f44336' }}>
                    {attendanceHistory.summary.absentDays}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Absent Days
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card variant="outlined">
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h3" sx={{ fontWeight: 'bold', mb: 1, color: '#ff9800' }}>
                    {attendanceHistory.summary.totalOvertime}h
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Total Overtime
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

        </Box>

        <Paper variant="outlined" sx={{ p: 2, mb: 4 }}>
          <Box sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 2,
            justifyContent: 'space-between'
          }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, flex: 1, pr: 2 }}>
              <FormControl size="small" sx={{ minWidth: 150, flex: 1 }}>
                <InputLabel>Status</InputLabel>
                <Select
                  value={filters.status}
                  label="Status"
                  onChange={(e) => updateFilter('status', e.target.value)}
                >
                  <MenuItem value="all">All Status</MenuItem>
                  <MenuItem value="present">Present</MenuItem>
                  <MenuItem value="absent">Absent</MenuItem>
                  <MenuItem value="leave">Leave</MenuItem>
                  <MenuItem value="early out">Early Out</MenuItem>
                  <MenuItem value="weekend">Weekend</MenuItem>
                  <MenuItem value="holiday">Holiday</MenuItem>
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ minWidth: 120, flex: 1 }}>
                <InputLabel>Year</InputLabel>
                <Select
                  value={filters.year}
                  label="Year"
                  onChange={(e) => updateFilter('year', e.target.value)}
                >
                  <MenuItem value="all">Select Years</MenuItem>
                  <MenuItem value="2026">2026</MenuItem>
                  <MenuItem value="2025">2025</MenuItem>
                  <MenuItem value="2024">2024</MenuItem>
                  <MenuItem value="2023">2023</MenuItem>
                  <MenuItem value="2022">2022</MenuItem>
                </Select>
              </FormControl>

              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  views={['month', 'year']}
                  label="Select Month"
                  value={selectedMonth}
                  onChange={(newValue) => setSelectedMonth(newValue)}
                  slotProps={{ textField: { size: 'small', fullWidth: true } }}
                  sx={{ minWidth: 180, flex: 1 }}
                />
              </LocalizationProvider>

              <TextField
                size="small"
                label="Search..."
                value={filters.search}
                onChange={(e) => updateFilter('search', e.target.value)}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ color: 'text.secondary', mr: 1 }} />,
                }}
                sx={{ minWidth: 180, flex: 1 }}
              />
            </Box>

            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                startIcon={<RefreshIcon />}
                onClick={fetchAttendanceHistory}
                disabled={loading}
                variant="outlined"
                size="small"
              >
                Refresh
              </Button>
              <Button
                startIcon={<DownloadIcon />}
                onClick={handleExportData}
                variant="outlined"
                size="small"
              >
                Export
              </Button>
              <Button
                startIcon={<PrintIcon />}
                onClick={handlePrintReport}
                variant="outlined"
                size="small"
              >
                Print
              </Button>
            </Box>
          </Box>
        </Paper>

        <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
            Detailed Attendance Records
          </Typography>

          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Day</TableCell>
                  <TableCell>Check-in</TableCell>
                  <TableCell>Check-out</TableCell>
                  <TableCell>Work Hours</TableCell>
                  <TableCell>Overtime</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Location</TableCell>
                  <TableCell>Notes</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableSkeleton columns={9} rows={pagination.rowsPerPage} />
                ) : paginatedRecords.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 6 }}>
                      <SearchIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
                      <Typography variant="body1" color="textSecondary">
                        No attendance records found
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        Try adjusting your filters or check a different month
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedRecords.map((record) => (
                    <TableRow key={record.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {formatDate(record.date, 'MMM d')}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {formatDate(record.date)}
                        </Typography>
                      </TableCell>

                      <TableCell>
                        <Chip
                          label={record.day}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>

                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {record.checkIn !== '-' ? (
                            <>
                              <InIcon fontSize="small" color="action" />
                              <Typography>{record.checkIn}</Typography>
                            </>
                          ) : (
                            <Typography color="textSecondary">-</Typography>
                          )}
                        </Box>
                      </TableCell>

                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {record.checkOut !== '-' ? (
                            <>
                              <OutIcon fontSize="small" color="action" />
                              <Typography>{record.checkOut}</Typography>
                            </>
                          ) : (
                            <Typography color="textSecondary">-</Typography>
                          )}
                        </Box>
                      </TableCell>

                      <TableCell>
                        <Typography>
                          {record.workHours > 0 ? `${record.workHours} hrs` : '-'}
                        </Typography>
                      </TableCell>

                      <TableCell>
                        <Typography
                          color={record.overtime > 0 ? 'success.main' : 'textSecondary'}
                          fontWeight={record.overtime > 0 ? 'medium' : 'normal'}
                        >
                          {record.overtime > 0 ? `+${record.overtime} hrs` : '-'}
                        </Typography>
                      </TableCell>

                      <TableCell>
                        <StatusChip status={record.status} />
                      </TableCell>

                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          {record.location !== '-' && (
                            <LocationIcon fontSize="small" color="action" />
                          )}
                          <Typography variant="body2">
                            {record.location}
                          </Typography>
                        </Box>
                      </TableCell>

                      <TableCell>
                        <Tooltip title={record.notes}>
                          <Typography
                            variant="body2"
                            sx={{
                              maxWidth: 150,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              cursor: 'pointer'
                            }}
                          >
                            {record.notes}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={totalRecords}
            rowsPerPage={pagination.rowsPerPage}
            page={pagination.page}
            onPageChange={pagination.handleChangePage}
            onRowsPerPageChange={pagination.handleChangeRowsPerPage}
          />

          <Alert severity="info" sx={{ mt: 3 }}>
            <Typography variant="body2">
              <strong>Note:</strong> This history includes all recorded attendance entries.
              Weekends and holidays are marked accordingly. For any discrepancies,
              please contact your HR department.
            </Typography>
          </Alert>
        </Paper>

        <Paper variant="outlined" sx={{ p: 3, mb: 4 }}>
          <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
            Monthly Attendance Overview
          </Typography>
          {attendanceHistory.monthlyData.length === 0 ? (
            <Box sx={{ height: 200, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
              <BackIcon sx={{ fontSize: 48, opacity: 0.2, mb: 1 }} />
              <Typography variant="body2" color="textSecondary">No monthly data available yet</Typography>
              <Typography variant="caption" color="textSecondary">Data will appear once attendance is recorded</Typography>
            </Box>
          ) : (
            <Box sx={{ width: '100%', height: 300, display: 'flex', justifyContent: 'center', overflowX: 'auto' }}>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={attendanceHistory.monthlyData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <RechartsTooltip />
                  <Legend />
                  <Bar dataKey="present" name="Present Days" fill="#4caf50" />
                  <Bar dataKey="absent" name="Absent Days" fill="#f44336" />
                  <Bar dataKey="late" name="Late Arrivals" fill="#ff9800" />
                </BarChart>
              </ResponsiveContainer>
            </Box>
          )}
        </Paper>

        <Grid sx={{ mt: 3 }}>
          <Grid size={{ xs: 12, md: 6 }} mb={3}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Attendance Summary
                </Typography>
                <Grid container spacing={2}>
                  {[
                    { label: 'Total Working Days', value: attendanceHistory.summary.totalWorkingDays, color: 'inherit' },
                    { label: 'Present Days', value: attendanceHistory.summary.presentDays, color: '#4caf50' },
                    { label: 'Absent Days', value: attendanceHistory.summary.absentDays, color: '#f44336' },
                    { label: 'Late Arrivals', value: attendanceHistory.summary.lateArrivals, color: '#ff9800' },
                    { label: 'Leaves Taken', value: attendanceHistory.summary.leavesTaken, color: '#2196f3' },
                  ].map((item, index) => (
                    <Grid size={{ xs: 6, sm: 4 }} key={index}>
                      <Box sx={{ p: 1.5, border: '1px solid #eee', borderRadius: 1, height: '100%' }}>
                        <Typography variant="body2" color="textSecondary">
                          {item.label}
                        </Typography>
                        <Typography
                          variant="h6"
                          sx={{
                            color: item.color || 'inherit',
                            fontWeight: 'bold'
                          }}
                        >
                          {item.value}
                        </Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Performance Metrics
                </Typography>
                <Box sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2">Attendance Rate</Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {calculateAttendanceRate()}%
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={calculateAttendanceRate()}
                    sx={{ height: 8, borderRadius: 4 }}
                  />
                </Box>

                <Box sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2">Average Work Hours/Day</Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {attendanceHistory.summary.averageWorkHours}h
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={(attendanceHistory.summary.averageWorkHours / 12) * 100} // Based on 12h scale
                    sx={{ height: 8, borderRadius: 4 }}
                  />
                </Box>

                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2">Total Overtime</Typography>
                    <Typography variant="body2" fontWeight="bold" color="success.main">
                      +{attendanceHistory.summary.totalOvertime}h
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={(attendanceHistory.summary.totalOvertime / 20) * 100}
                    sx={{ height: 8, borderRadius: 4 }}
                  />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Paper>
    </Box >
  )
}

export default AttendanceHistory